import { formatRecordedDate, compareRecordedDates } from '@/utils/recordedValues'
/**
 * UserTable Component
 * Displays a list of users with selection, sorting, and actions
 */

import { useState } from 'react'
import {
  MoreHorizontal,
  Mail,
  Shield,
  UserCog,
  Trash2,
  ChevronUp,
  ChevronDown,
  Check,
  Users as UsersIcon,
  UserX,
  UserCheck,
} from 'lucide-react'
import type { User } from '@/types/user.types'
import { getRoleDisplayName, getRoleBadgeClass } from '@/types/auth.types'
import { formatRelativeTime } from '@/types/session.types'

// ============================================================================
// TYPES
// ============================================================================

type SortField = 'fullName' | 'email' | 'role' | 'lastLoginAt' | 'createdAt'
type SortOrder = 'asc' | 'desc'

interface UserTableProps {
  users: User[]
  isLoading?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  onEdit?: (user: User) => void
  onDelete?: (user: User) => void
  onRoleChange?: (user: User) => void
  onResendInvite?: (user: User) => void
  onToggleStatus?: (user: User) => void
  currentUserId?: string
}

// ============================================================================
// COMPONENT
// ============================================================================

export function UserTable({
  users,
  isLoading = false,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onRoleChange,
  onResendInvite,
  onToggleStatus,
  currentUserId,
}: UserTableProps) {
  const [sortField, setSortField] = useState<SortField>('fullName')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    let aVal: string | number | undefined
    let bVal: string | number | undefined

    switch (sortField) {
      case 'fullName':
        aVal = a.fullName.toLowerCase()
        bVal = b.fullName.toLowerCase()
        break
      case 'email':
        aVal = a.email.toLowerCase()
        bVal = b.email.toLowerCase()
        break
      case 'role':
        aVal = a.role
        bVal = b.role
        break
      case 'lastLoginAt':
        return compareRecordedDates(a.lastLoginAt, b.lastLoginAt, sortOrder)
      case 'createdAt':
        return compareRecordedDates(a.createdAt, b.createdAt, sortOrder)
    }

    if (aVal === undefined || bVal === undefined) return 0
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
    return 0
  })

  // Handle sort
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  // Handle selection
  const isAllSelected = users.length > 0 && selectedIds.length === users.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < users.length

  const handleSelectAll = () => {
    if (!onSelectionChange) return
    if (isAllSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(users.map(u => u.userId))
    }
  }

  const handleSelectOne = (userId: string) => {
    if (!onSelectionChange) return
    if (selectedIds.includes(userId)) {
      onSelectionChange(selectedIds.filter(id => id !== userId))
    } else {
      onSelectionChange([...selectedIds, userId])
    }
  }

  // Sort header component
  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-primary-600 transition-colors"
    >
      {children}
      {sortField === field &&
        (sortOrder === 'asc' ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        ))}
    </button>
  )

  // Get user initials
  const getInitials = (name: string) =>
    name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)

  // Get status badge
  const getStatusBadge = (user: User) => {
    if (user.isActive == null || user.isVerified == null) {
      return <span className="badge badge-ghost badge-sm">Not recorded</span>
    }
    if (user.isActive === false) {
      return <span className="badge badge-error badge-sm">Inactive</span>
    }
    if (user.isVerified === false) {
      return <span className="badge badge-warning badge-sm">Pending</span>
    }
    return <span className="badge badge-success badge-sm">Active</span>
  }

  if (isLoading) {
    return (
      <div className="card card-elevated overflow-hidden">
        <div className="divide-y divide-surface-overlay">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="skeleton w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-40 rounded" />
                <div className="skeleton h-3 w-56 rounded" />
              </div>
              <div className="skeleton h-6 w-20 rounded-full" />
              <div className="skeleton h-6 w-16 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
          <UsersIcon className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-2">No users found</p>
        <p className="text-text-secondary">Try adjusting your search or filters</p>
      </div>
    )
  }

  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table table-sticky-head w-full">
          <thead className="bg-surface-overlay">
            <tr className="text-sm font-semibold text-text-primary">
              {onSelectionChange && (
                <th className="w-12">
                  <label className="cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary checkbox-sm"
                      checked={isAllSelected}
                      ref={el => {
                        if (el) el.indeterminate = isSomeSelected
                      }}
                      onChange={handleSelectAll}
                    />
                  </label>
                </th>
              )}
              <th>
                <SortHeader field="fullName">User</SortHeader>
              </th>
              <th>
                <SortHeader field="role">Role</SortHeader>
              </th>
              <th>Status</th>
              <th>
                <SortHeader field="lastLoginAt">Last Login</SortHeader>
              </th>
              <th>
                <SortHeader field="createdAt">Joined</SortHeader>
              </th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-overlay">
            {sortedUsers.map(user => {
              const isSelected = selectedIds.includes(user.userId)
              const isCurrentUser = user.userId === currentUserId

              return (
                <tr
                  key={user.userId}
                  className={`hover:bg-surface-overlay/50 transition-colors ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {onSelectionChange && (
                    <td>
                      <label className="cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={isSelected}
                          onChange={() => handleSelectOne(user.userId)}
                        />
                      </label>
                    </td>
                  )}
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-medium">
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.fullName}
                            className="w-full h-full rounded-full"
                          />
                        ) : (
                          getInitials(user.fullName)
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p
                            className="font-medium text-text-primary hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer"
                            onClick={() => onEdit?.(user)}
                          >
                            {user.fullName}
                          </p>
                          {isCurrentUser && (
                            <span className="badge badge-primary badge-xs">You</span>
                          )}
                        </div>
                        <p className="text-sm text-text-tertiary">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td>{getStatusBadge(user)}</td>
                  <td>
                    <span className="text-sm text-text-secondary">
                      {user.lastLoginAt ? formatRelativeTime(user.lastLoginAt) : 'Never'}
                    </span>
                  </td>
                  <td>
                    <span className="text-sm text-text-secondary">
                      {formatRecordedDate(user.createdAt, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </td>
                  <td>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenMenuId(openMenuId === user.userId ? null : user.userId)
                        }
                        className="btn btn-ghost btn-sm btn-square"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>

                      {openMenuId === user.userId && (
                        <>
                          {/* Backdrop to close menu */}
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />

                          {/* Menu */}
                          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-surface-dark rounded-xl shadow-xl border border-surface-overlay z-20 animate-in fade-in slide-in-from-top-1 duration-150">
                            <div className="py-1">
                              {onEdit && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    onEdit(user)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                                >
                                  <UserCog className="w-4 h-4" />
                                  Edit User
                                </button>
                              )}

                              {onRoleChange && !isCurrentUser && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    onRoleChange(user)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                                >
                                  <Shield className="w-4 h-4" />
                                  Change Role
                                </button>
                              )}

                              {onResendInvite && user.isVerified === false && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    onResendInvite(user)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                                >
                                  <Mail className="w-4 h-4" />
                                  Resend Invite
                                </button>
                              )}

                              {onToggleStatus && !isCurrentUser && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    onToggleStatus(user)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                                >
                                  {user.isActive ? (
                                    <>
                                      <UserX className="w-4 h-4 text-amber-500" />
                                      Deactivate User
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 text-emerald-500" />
                                      Activate User
                                    </>
                                  )}
                                </button>
                              )}

                              {onDelete && !isCurrentUser && (
                                <>
                                  <div className="border-t border-surface-overlay my-1" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenMenuId(null)
                                      onDelete(user)
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete User
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Selection info bar */}
      {selectedIds.length > 0 && (
        <div className="px-4 py-3 bg-primary-50 dark:bg-primary-900/20 border-t border-surface-overlay flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-primary-600" />
            <span className="text-sm text-text-primary">
              {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <button
            type="button"
            onClick={() => onSelectionChange?.([])}
            className="text-sm text-primary-600 hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  )
}

export default UserTable
